import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { matchIsValidTel, TelInput } from '@center/ui-kit';
import { Typography, Dialog, Stack, useTheme, Button, Skeleton, TextField } from '@mui/material'
import { FormProvider, Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom';
import { object, string } from 'yup'
import { useNavigate } from 'router';
import { yupResolver } from '@hookform/resolvers/yup';

import { trackEvent } from 'analytics'
import ModalHeader from 'components/global/Modals/ModalHeader'
import { toast } from 'components/global/CUK/Toast/Toast';
import { OPERATION_UPDATE_COUPON } from 'pages/Coupons/operations-gql/mutations';
import { OPERATION_FIND_COUPON_STANDARD } from 'pages/Coupons/operations-gql/queries';

import ErrorMessage from '../Form/ErrorMessage';

const buildSchema = ({ t }) => {
  return object({
    phone: string().test(
      'phone-validation',
      t('ui_elements.invalid_format_field'),
      (value) => value === '' || matchIsValidTel(value ?? '')
    ),
    description: string().required(t('ui_elements.required_field')),
  })
}

const Form = ({ coupon }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const validationSchema = buildSchema({ t });
  const [update, {loading}] = useMutation(
    OPERATION_UPDATE_COUPON,
    {
      onCompleted: () => {
        toast.success(t('coupons.actions.change_phone.toast_success'));
        navigate({ pathname: '..' })
      },
      onError: () => {
        toast.error(t('coupons.actions.change_phone.toast_error'));
      },
    },
  );
  const methods = useForm({
    defaultValues: {
      phone: coupon.phone || '',
      description: coupon.description,
    },
    resolver: yupResolver(validationSchema)
  })

  const handleConfirm = async ({ phone = '', description }) => {
    const { data } = await update({
      variables: {
        id: +coupon.id!,
        phone_number: phone.length ? phone : null,
        description: description,
      }
    })
    if (!data) return

    trackEvent('code_updated', {
      code_type: 'standard',
      phone_changed: phone !== (coupon.phone || ''),
      description_changed: description !== coupon.description,
    })
  }

  return (
    <FormProvider {...methods}>
      <Stack gap={1}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography sx={{ fontSize: 13 }}>{t('coupons.create.phone')}</Typography>
          <ErrorMessage message={methods.formState.errors.phone?.message as string} />
        </Stack>

        <Controller
          control={methods.control}
          name="phone"
          render={({ field: { value, onChange } }) => (
            <TelInput
              defaultCountry="FR"
              value={value}
              onChange={(value) => {
                console.log(value)
                onChange(value);
              }}
              size="small"
            />
          )}
        />
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography sx={{ fontSize: 13 }}>{t('coupons.create.description')}</Typography>
          <ErrorMessage message={methods.formState.errors.description?.message as string} />
        </Stack>
        <Controller
          control={methods.control}
          name="description"
          render={({ field }) => (
            <TextField
              {...field}
              rows={8}
              multiline
              placeholder={t('coupons.create.description_placeholder')}
            />
          )}
        />
      </Stack>
      <Stack direction={'row'} justifyContent={'space-between'} mt={1}>
        <Button
          size={'small'}
          variant={'outlined'}
          onClick={() => navigate({ pathname: '..' })}
        >
          {t('ui_elements.cancel')}
        </Button>
        <Button
          size={'small'}
          variant={'contained'}
          color={'primary'}
          onClick={methods.handleSubmit(handleConfirm)}
          disabled={loading}
        >
          {t('coupons.actions.change_phone.change')}
        </Button>
      </Stack>
    </FormProvider>
  )
}

const Loader = () => {
  return (
    <Stack gap={1}>
      <Skeleton variant={'text'} width={200} />
      <Skeleton variant={'rectangular'} width={'100%'} height={32} sx={{ borderRadius: 1 }} />
      <Skeleton variant={'text'} width={200} />
      <Skeleton variant={'rectangular'} width={'100%'} height={162} sx={{ borderRadius: 1 }} />
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Skeleton variant={'rectangular'} width={100} height={32} sx={{ borderRadius: 1 }} />
        <Skeleton variant={'rectangular'} width={100} height={32} sx={{ borderRadius: 1 }} />
      </Stack>
    </Stack>
  )
}

const Edit = () => {
  const { id } = useParams();
  const theme = useTheme();
  const { data, loading } = useQuery(
    OPERATION_FIND_COUPON_STANDARD,
    {
      variables: {
        id: +id!,
      },
      fetchPolicy: 'no-cache'
    },
  );
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Dialog
      style={{ backgroundColor: 'transparent' }}
      maxWidth='md'
      PaperProps={{
        style: {
          width: '480px',
          borderRadius: '24px',
          padding: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh'
        }
      }}
      open
    >
      <Stack>
        <ModalHeader
          subtitle={t('coupons.modal.edit_title')}
          title={t('coupons.modal.edit_subtitle', { code: data?.coupon?.code })}
          icon={<i className={'fa-light fa-pen'} style={{ color: theme.palette.primary.main }} />}
          onClose={() => navigate({ pathname: '..' })}
        />
        <Stack m={4} mt={1}>
          {
            loading
              ? <Loader />
              : <Form coupon={data.coupon} />
          }
        </Stack>
      </Stack>
    </Dialog>
  )
}

export default Edit;
